<template>
  <Teleport to="body">
    <div v-if="isMobile">
      <SidebarMobile />
    </div>
    <div v-else>
      <div 
        v-if="isAtHeaderSection"
        class="tw-absolute tw-top-0 tw-z-[51] tw-p-10 tw-cursor-pointer"
        :style="{
          background: isDarkMode ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(12px)',
          left: `${sideMargin}px`,
        }"
        @click="scrollToTop"
      >
        <img :src="$cdn(isDarkMode ? logoDark : logoLight)">
      </div>
      <!-- Collapsed Sidebar (shown when sidebar is closed) -->
      <section v-else>
        <div
          ref="collapsedSidebarRef"
          class="sidebar-collapsed tw-fixed tw-top-[104px] tw-z-[51] animate__animated animate__faster tw-cursor-pointer animate__fadeInDown"
          :style="{
            left: `${sideMargin}px`,
          }" 
          @click="toggleSidebar"
        >
          <img
            :src="collapsedSidebar"
            alt="Collapsed Sidebar"
            class="tw-h-14 tw-w-[58px]"
            :class="{
              'tw-bg-white dark:tw-bg-black': activeSection === 'our-work', // Set to dark mode when 'our-work'
              'tw-bg-black dark:tw-bg-white': activeSection !== 'our-work', // Otherwise, use light mode
            }"
          >
        </div>

        <!-- Sidebar (open state) -->
        <div 
          ref="expandedSidebarRef"
          class="sidebar-expanded animate__animated animate__faster tw-pointer-events-none" 
          :style="{ left: `${sideMargin}px` }" 
          :class="[
            'tw-opacity-0',
            'tw-fixed tw-max-w-[240px] tw-max-h-[480px] tw-z-[50] tw-bg-white dark:tw-bg-black',
          ]"
        >
          <div class="tw-pl-6 tw-pr-8 tw-pt-[40px] tw-pb-[32px] tw-bg-white dark:tw-bg-black">
            <!-- Sidebar Logo -->
            <div>
              <img
                :src="$cdn(isDarkMode ? 'images/home/logo_dark.svg' : 'images/home/logo_light.svg')"
                alt="Sidebar Logo"
                class="tw-w-[135px] tw-h-[40px] tw-cursor-pointer"
                @click="scrollToTop"
              >
            </div>

            <!-- Navigation Links -->
            <div class="tw-flex tw-flex-col tw-pt-18">
              <a
                v-for="(section, index) in sections"
                :key="index"
                :href="`#${section}`"
                class=""
                :class="[
                  'tw-text-label-bold-desktop tw-tracking-[0.75px] tw-uppercase tw-py-2 tw-flex tw-items-center',
                  activeSection === section ? 'tw-text-ap-persian-red' : 'tw-text-ap-nero dark:tw-text-white hover:tw-text-ap-persian-red',
                  activeSection !== section ? 'hover:tw-text-ap-persian-red dark:hover:tw-text-ap-persian-red' : '',]"
                @click="scrollToSection(section, $event)"
              >
                <span
                  v-show="activeSection === section"
                  class="active-section-indicator animate__animated"
                  :class="{
                    'opacity-100 transform animate__zoomIn ': activeSection === section, // Apply zoomIn animation when shown
                    'tw-transform-translate-x-[-100%] opacity-0 transform  animate__zoomOut': activeSection !== section, // Move it off screen when closing
                  }"
                />
                <span
                  :class="{
                    'tw-translate-x-[4%] tw-duration-[800ms]':
                      activeSection === section, // Animate shift when first span is shown
                  }"
                >
                  {{ sectionLabel(section) }}
                </span>
              </a>
            </div>

            <!-- Toggle and Top Images -->
            <div class="tw-flex tw-gap-4 tw-pt-18">
              <DaylightButton
                v-model:dark-mode="darkMode"
                :class="{
                  'tw-cursor-not-allowed tw-opacity-50': activeSection === 'our-work',
                  'tw-opacity-100 hover:tw-opacity-80 hover:tw-scale-105': activeSection !== 'our-work',
                  'tw-transition-all tw-duration-[500ms] tw-ease-in-out': true,
                }" 
                :style="{
                  pointerEvents: activeSection === 'our-work' ? 'none' : 'auto',
                }"
              />
              <!-- <img ref="darkModeToggleImage"
                :src="$cdn(isDarkMode ? 'images/home/toggle-dark.svg' : 'images/home/toggle-light.svg')" alt="Toggle"
                class="tw-w-[80px] tw-h-[40px] tw-flex-shrink-0 tw-cursor-pointer" :class="{
                  'tw-cursor-not-allowed tw-opacity-50':
                    activeSection === 'our-work',
                  'tw-opacity-100 hover:tw-opacity-80 hover:tw-scale-105':
                    activeSection !== 'our-work',
                  'tw-transition-all tw-duration-[500ms] tw-ease-in-out': true,
                  'opacity-toggle': isToggled,
                }" @click="darkModeToggle" :disabled="activeSection === 'our-work'" :style="{
                  pointerEvents: activeSection === 'our-work' ? 'none' : 'auto',
                }" /> -->
              <img
                :src="$cdn(isDarkMode ? 'images/home/top-dark.svg' : 'images/home/top-light.svg')"
                alt="Top"
                class="tw-w-[40px] tw-h-[40px] tw-flex-shrink-0 tw-cursor-pointer hover:tw-opacity-80 hover:tw-scale-105"
                :class="{
                  'tw-transition-all tw-duration-[1000ms] tw-ease-in-out': true, // Set transition duration to 5 seconds
                }"
                @click="scrollToTop"
              >
            </div>
          </div>
          
        </div>
      </section>
    </div>
  </Teleport>
</template>


<script setup lang="ts">
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { cdn } from '../../support/cdn';
import { scrollToTop, watchDarkMode } from '../../support/helper';
import DaylightButton from '../shared/DaylightButton.vue';
import SidebarMobile from './sidebar-mobile.vue';

gsap.registerPlugin(ScrollToPlugin);

// Dark Mode State
const darkMode = ref(false);
const sections = ['about-us', 'our-services', 'our-work', 'get-in-touch'];
const isDirectCollapse = ref(false);
const logoDark = cdn('images/home/logo_dark.svg');
const logoLight = cdn('images/home/logo_light.svg');
const isMobile = ref(false);
const isSidebarOpen = ref(false);
const collapsedSidebar = cdn('images/home/collapsed_sidebar.svg');
const isToggled = ref(false);
const activeSection = ref('');
const containerWidth = ref(0);
const isDarkMode = ref(false);

const collapsedSidebarRef = ref<HTMLElement | null>();
const expandedSidebarRef = ref<HTMLElement | null>();

let stopObserving: () => void;

defineProps({
  isAtHeaderSection: {
    type: Boolean,
    default: true
  },
});

const updateContainerWidth = () => {
  containerWidth.value = window.innerWidth;
};

const scrollToSection = (section: string, event: MouseEvent) => {
  event.preventDefault();
  const element = document.getElementById(section);

  if (element) {
    // Give time for layout/GSAP transform to apply
    requestAnimationFrame(() => {
      const rect = element.getBoundingClientRect();
      const y = rect.top + window.scrollY;

      gsap.to(window, {
        scrollTo: { y },
        duration: 1,
        ease: 'power2.out',
        onComplete: () => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }
};

const sectionLabel = (section: string) => {
  switch (section) {
    case 'about-us':
      return 'About Us';
    case 'our-services':
      return 'Our Services';
    case 'our-work':
      return 'Our Work';
    case 'get-in-touch':
      return 'Get in Touch';
    default:
      return '';
  }
};

const updateActiveSection = (section: string) => {
  activeSection.value = section;
};

const checkMobileSize = () => {
  isMobile.value = window.innerWidth <= 1280;
};

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
};

const calculateSideMargin = () => {
  if (window.innerWidth > 1280 && window.innerWidth <= 1440) {
    return 100;
  }
  if (window.innerWidth > 1440 && window.innerWidth <= 1920) {
    return 320;
  }
  if (window.innerWidth <= 1280) {
    return (window.innerWidth - 1280) / 2;
  }
  return 300;
};

const sideMargin = ref(calculateSideMargin());
const updateSideMargin = () => {
  isMobile.value = window.innerWidth <= 1280;
  sideMargin.value = calculateSideMargin();
};

const darkModeToggle = () => {
  isToggled.value = true;
  setTimeout(() => {
    darkMode.value = !darkMode.value;
  }, 300);

  setTimeout(() => {
    isToggled.value = false;
  }, 300); // Match the full animation duration
};

watch(darkMode, (newVal) => {
  if (newVal && activeSection.value !== 'get-in-touch') {
    document.documentElement.classList.add('dark');
    // console.warn("Dark mode enabled.");
  } else {
    document.documentElement.classList.remove('dark');
    // console.warn("Dark mode disabled.");
  }
});

watch(activeSection, (section) => {
  // Disable dark mode when entering "get-in-touch"
  if (section === 'get-in-touch') {
    darkMode.value = false;
  }
});

watch(isSidebarOpen, (isOpen) => {
  if (isOpen) {
    // Enter animation
    collapsedSidebarRef.value?.classList.remove('animate__fadeInDown');
    collapsedSidebarRef.value?.classList.add('animate__fadeOutUp');
    collapsedSidebarRef.value?.classList.add('tw-pointer-events-none');

    expandedSidebarRef.value?.classList.remove('animate__fadeOutUp');
    expandedSidebarRef.value?.classList.add('animate__fadeInDown');
    expandedSidebarRef.value?.classList.remove('tw-pointer-events-none');
  } else {
    // Exit animation
    collapsedSidebarRef.value?.classList.remove('animate__fadeOutUp');
    collapsedSidebarRef.value?.classList.add('animate__fadeInDown');
    collapsedSidebarRef.value?.classList.remove('tw-pointer-events-none');

    expandedSidebarRef.value?.classList.remove('animate__fadeInDown');
    expandedSidebarRef.value?.classList.add('animate__fadeOutUp');
    expandedSidebarRef.value?.classList.add('tw-pointer-events-none');
  }
});


// Handle scroll behavior and active section logic
const observeSections = () => {
  const updateOnScroll = () => {
    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const isInView = rect.top >= 0 && rect.top <= window.innerHeight / 2;

        if (isInView) {
          updateActiveSection(sectionId);
        }
      }
    });

    // Ensure dark mode is disabled when "get-in-touch" is in view
    if (activeSection.value === 'get-in-touch') {
      darkMode.value = false;
    }
  };

  window.addEventListener('scroll', updateOnScroll);
  updateOnScroll();
};

// Dark mode watcher to add/remove the class
watch(darkMode, (newVal) => {
  if (newVal) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
});

const handleScroll = () => {
  if (isSidebarOpen.value && window.scrollY > 100) {
    // Collapse the sidebar when scrolling down, only if it's open
    isDirectCollapse.value = true;
    isSidebarOpen.value = false;  // Collapse the sidebar
  } else if (window.scrollY <= 100 && !isSidebarOpen.value && !isDirectCollapse.value) {
    // Keep the sidebar collapsed if it's not toggled manually
    isSidebarOpen.value = false;
  }
};

onMounted(() => {
  window.addEventListener('resize', updateSideMargin);
  window.addEventListener('scroll', handleScroll);
  updateSideMargin();
  nextTick(() => {
    observeSections();
  });
  updateContainerWidth();
  window.addEventListener('resize', updateContainerWidth);
  checkMobileSize();
  stopObserving = watchDarkMode((value: any) => {
    darkMode.value = value;
    isDarkMode.value = value;
    isToggled.value = value;
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', updateContainerWidth);
  window.removeEventListener('resize', updateSideMargin);
  window.removeEventListener('scroll', handleScroll);
  if (stopObserving) stopObserving();
});
</script>

<style scoped>
.opacity-toggle {
  animation: opacity-toggle-animation 500ms ease-in-out;
}

@keyframes opacity-toggle-animation {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }

  100% {
    opacity: 1;
  }
}

.active-section-indicator {
  background-color: #dc1e1e;
  width: 32px;
  height: 2px;
  opacity: 0;
}

.animate__animated.animate__zoomIn {
  --animate-duration: 800ms;
}

.animate__animated.animate__zoomOut {
  --animate-duration: 800ms;
}


a .active-section-indicator {
  opacity: 1;
}
</style>
